import { type CSSProperties, forwardRef } from 'react';
import Typography from '../Typography';

interface Props extends React.HTMLProps<HTMLInputElement> {
  name: string;
  defaultValue?: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  helperText?: string | React.ReactNode;
  errorText?: string;
  type?: string;
  value?: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  textColor?: CSSProperties['color'];
}

const Input = forwardRef((props: Props, _ref: React.ForwardedRef<HTMLInputElement>) => {
  const {
    name,
    defaultValue,
    placeholder,
    label,
    disabled,
    onFocus,
    onBlur,
    onChange,
    onKeyDown,
    helperText,
    errorText,
    type = 'text',
    required = true,
    value,
    textColor,
  } = props;

  return (
    <div className="space-y-1">
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 text-wt-text-on-background"
          style={{ color: textColor }}
        >
          {label}
        </label>
      )}
      <div className="mt-1">
        <input
          type={type}
          name={name}
          id={name}
          value={value}
          required={required}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={(e) => onFocus?.(e)}
          onBlur={(e) => onBlur?.(e)}
          onChange={(e) => onChange?.(e)}
          onKeyDown={(e) => onKeyDown?.(e)}
          className="block w-full rounded-wt border-gray-300 bg-transparent text-sm shadow-sm placeholder:text-sm focus:border-wt-primary focus:ring-wt-primary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
          style={{ color: textColor }}
        />
        {helperText && (
          <Typography size="xs" weight="light" className={'block leading-snug'}>
            {helperText}
          </Typography>
        )}
        {errorText && (
          <Typography size="xs" weight="light" color="red" className="block leading-snug">
            {errorText}
          </Typography>
        )}
      </div>
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
